import React, { useState } from 'react';
import { useReviews } from '../context/ReviewsContext';
import StarRating from '../components/StarRating';
import Loader from '../components/Loader';

const ReviewsPage = () => {
  const { reviews, loading } = useReviews();
  const [sortBy, setSortBy] = useState('newest');
  
  // Sort reviews
  const sortedReviews = [...reviews].sort((a, b) => {
    switch (sortBy) {
      case 'newest':
        return new Date(b.createdAt) - new Date(a.createdAt);
      case 'highest':
        return b.rating - a.rating;
      case 'helpful':
        return b.helpful - a.helpful;
      default:
        return new Date(b.createdAt) - new Date(a.createdAt);
    }
  });

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-NG', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const averageRating = reviews.length > 0 
    ? (reviews.reduce((sum, review) => sum + review.rating, 0) / reviews.length).toFixed(1)
    : 0;

  return loading ? <Loader text="Loading reviews..." /> : (
    <div className="main-container reviews-page">
      {/* Hero Section */}
      <section style={{
        background: 'linear-gradient(135deg, var(--nigeria-green), var(--warm-orange))',
        color: 'white',
        padding: '4rem 0',
        textAlign: 'center'
      }}>
        <div className="container">
          <h1 style={{ 
            fontSize: '3.5rem', 
            marginBottom: '1rem',
            fontWeight: '900'
          }}>
            ⭐ Customer Reviews
          </h1>
          <p style={{ 
            fontSize: '1.3rem', 
            marginBottom: '2rem'
          }}>
            See what our customers are saying about TiwaTinwa's authentic Nigerian cuisine!
          </p>
          
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            gap: '3rem',
            marginTop: '2rem',
            flexWrap: 'wrap'
          }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '2.5rem', fontWeight: '800' }}>
                {reviews.length}
              </div>
              <div>Total Reviews</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '2.5rem', fontWeight: '800' }}>
                {averageRating}
              </div>
              <div>Average Rating</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '2.5rem', fontWeight: '800' }}>
                98%
              </div>
              <div>Satisfied Customers</div>
            </div>
          </div>
        </div>
      </section>

      {/* Reviews Content */}
      <section className="section">
        <div className="container">
          {/* Sort Controls */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '3rem',
            flexWrap: 'wrap',
            gap: '1rem'
          }}>
            <h2 style={{ 
              fontSize: '2rem',
              color: 'var(--nigeria-green)',
              fontWeight: '700',
              margin: 0
            }}>
              💬 All Reviews ({reviews.length})
            </h2>
            
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              style={{
                padding: '12px 15px',
                borderRadius: '10px',
                border: '2px solid #e9ecef',
                fontSize: '1rem',
                fontWeight: '500',
                cursor: 'pointer',
                background: 'white'
              }}
            >
              <option value="newest">🕐 Newest First</option>
              <option value="highest">⭐ Highest Rating</option>
              <option value="helpful">👍 Most Helpful</option>
            </select>
          </div>

          {/* Reviews Grid */}
          <div className="reviews-grid" style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
            gap: '2rem'
          }}>
            {sortedReviews.map(review => (
              <div key={review.id} style={{
                background: 'white',
                borderRadius: '20px',
                padding: '2rem',
                boxShadow: '0 8px 25px rgba(0,0,0,0.1)',
                border: '2px solid #f8f9fa',
                transition: 'all 0.3s ease'
              }}>
                {/* Review Header */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '1rem',
                  marginBottom: '1rem'
                }}>
                  <div style={{
                    fontSize: '2rem',
                    background: 'linear-gradient(135deg, var(--nigeria-green), var(--warm-orange))',
                    borderRadius: '50%',
                    width: '50px',
                    height: '50px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    {review.userAvatar}
                  </div>
                  
                  <div style={{ flex: 1 }}>
                    <h4 style={{ 
                      margin: 0,
                      fontSize: '1.1rem',
                      fontWeight: '700',
                      color: 'var(--text-dark)'
                    }}>
                      {review.userName}
                    </h4>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '1rem',
                      marginTop: '0.5rem'
                    }}>
                      <StarRating rating={review.rating} size="1rem" showNumber={false} />
                      <span style={{
                        color: 'var(--text-light)',
                        fontSize: '0.9rem'
                      }}>
                        {formatDate(review.createdAt)}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Review Title */}
                {review.title && (
                  <h5 style={{
                    fontSize: '1.2rem',
                    fontWeight: '600',
                    color: 'var(--nigeria-green)',
                    marginBottom: '1rem'
                  }}>
                    {review.title}
                  </h5>
                )}

                {/* Review Comment */}
                <p style={{
                  lineHeight: '1.6',
                  color: 'var(--text-dark)',
                  fontSize: '1rem',
                  marginBottom: '1rem'
                }}>
                  {review.comment}
                </p>

                {/* Review Actions */}
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  paddingTop: '1rem',
                  borderTop: '1px solid #f8f9fa'
                }}>
                  <span style={{
                    background: 'var(--nigeria-green)',
                    color: 'white',
                    padding: '4px 8px',
                    borderRadius: '10px',
                    fontSize: '0.8rem',
                    fontWeight: '600'
                  }}>
                    ✅ Verified Purchase
                  </span>
                  
                  <button style={{
                    background: 'none',
                    border: '2px solid #e9ecef',
                    borderRadius: '20px',
                    padding: '8px 15px',
                    fontSize: '0.9rem',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease'
                  }}>
                    👍 Helpful ({review.helpful})
                  </button>
                </div>
              </div>
            ))}
          </div>

          {reviews.length === 0 && (
            <div style={{
              textAlign: 'center',
              padding: '4rem 2rem',
              background: 'white',
              borderRadius: '20px',
              boxShadow: '0 8px 25px rgba(0,0,0,0.1)'
            }}>
              <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>⭐</div>
              <h3 style={{ 
                fontSize: '1.8rem', 
                marginBottom: '1rem',
                color: 'var(--text-dark)'
              }}>
                No Reviews Yet
              </h3>
              <p style={{ 
                fontSize: '1.1rem', 
                color: 'var(--text-light)'
              }}>
                Be the first to share your experience with TiwaTinwa!
              </p>
            </div>
          )}
        </div>
      </section>
      {/* Responsive styles for reviews page */}
      <style>{`
        .main-container.reviews-page,
        .main-container.reviews-page section,
        .main-container.reviews-page .container {
          box-sizing: border-box;
          width: 100%;
          max-width: 100vw;
          overflow-x: hidden;
        }
        .reviews-grid {
          width: 100%;
        }
        @media (max-width: 900px) {
          .main-container.reviews-page h1 {
            font-size: 2.2rem !important;
          }
          .main-container.reviews-page h2 {
            font-size: 1.3rem !important;
          }
        }
        @media (max-width: 600px) {
          .main-container.reviews-page .container {
            padding: 0 0.5rem !important;
          }
          .main-container.reviews-page .section {
            padding: 1rem 0 !important;
          }
          .reviews-grid {
            grid-template-columns: 1fr !important;
            gap: 1rem !important;
          }
        }
      `}</style>
    </div>
  );
};

export default ReviewsPage;